import { useCallback, useEffect, useState } from "react";

import { T } from "./strings";
import * as api from "./api/client";
import * as session from "./api/session";
import type { AuthUser } from "./api/types";
import { useAdvanced } from "./hooks/useAdvanced";
import { usePrefersReducedMotion, useTheme } from "./hooks/useTheme";
import { useWorkspace } from "./state/useWorkspace";
import { LoginScreen } from "./screens/LoginScreen";
import { MapScreen } from "./screens/MapScreen";

// Экран сверки УДАЛЁН 2026-09-05 (решение владельца): методология ушла из продукта в docs,
// цифра владельца — CSV /api/truth/agreement.csv. Карта — единственный экран за сессией.
type Session = { state: "checking" } | { state: "anonymous" } | { state: "in"; user: AuthUser };

export const LOGIN_PATH = "/login";

/**
 * The address bar, without a router.
 *
 * The app has two screens and switches between them with a tab, so a routing library would
 * be a sixth dependency bought for one path. `/login` still has to be a real URL — it is
 * what a 401 sends you to and what a bookmark or a hard refresh can land on — and nginx
 * already falls back to index.html for any path (ui/nginx.conf), so this is enough.
 */
const navigate = (path: string): void => {
  if (window.location.pathname !== path) window.history.replaceState(null, "", path);
};

export default function App() {
  const [auth, setAuth] = useState<Session>({ state: "checking" });
  const [notice, setNotice] = useState<string | null>(null);
  const { theme, toggle } = useTheme();
  const reducedMotion = usePrefersReducedMotion();

  // Boot: a stored token is a claim, not a session. Ask the server who it belongs to before
  // showing the map, or a stale token flashes a workspace that immediately 401s.
  useEffect(() => {
    let alive = true;
    if (!session.hasToken()) {
      setAuth({ state: "anonymous" });
      return;
    }
    void api
      .getMe()
      .then((user) => alive && setAuth({ state: "in", user }))
      .catch(() => alive && setAuth({ state: "anonymous" }));
    return () => {
      alive = false;
    };
  }, []);

  // One subscriber for the whole app: `request()` clears the token on any 401, and this is
  // what turns that into the login screen — no hook needs to know about authentication.
  useEffect(
    () =>
      session.subscribe((token) => {
        if (token !== null) return;
        setAuth((current) => {
          if (current.state === "in") setNotice(T.auth.sessionExpired);
          return { state: "anonymous" };
        });
      }),
    [],
  );

  useEffect(() => {
    navigate(auth.state === "anonymous" ? LOGIN_PATH : "/");
  }, [auth.state]);

  const signedIn = useCallback((user: AuthUser) => {
    setNotice(null);
    setAuth({ state: "in", user });
  }, []);

  if (auth.state === "checking") {
    return <div className="k-login k-login--checking">{T.common.loading}</div>;
  }
  if (auth.state === "anonymous") {
    return <LoginScreen onSignedIn={signedIn} notice={notice} />;
  }
  return <Workspace user={auth.user} theme={theme} toggleTheme={toggle} animate={!reducedMotion} />;
}

interface WorkspaceProps {
  user: AuthUser;
  theme: "dark" | "light";
  toggleTheme: () => void;
  animate: boolean;
}

/**
 * Everything behind the session. Split out so `useWorkspace` — which fetches on mount —
 * is not mounted at all while nobody is logged in: otherwise every page load would fire a
 * field list and a flags request that can only answer 401.
 */
function Workspace({ user, theme, toggleTheme, animate }: WorkspaceProps) {
  const workspace = useWorkspace();
  // Защёлка стоит здесь, читают её в консоли: значение живёт в одном модульном источнике,
  // поэтому оба держателя видят одно и то же (см. useAdvanced).
  const { advanced, toggleAdvanced } = useAdvanced();

  return (
    <div className="k-app">
      <header className="k-head">
        <div className="k-head__mark">
          <span className="k-head__title">{T.app.title}</span>
        </div>

        {/* Вкладка «Сверка» УДАЛЕНА 2026-09-05 (решение владельца): экран сверки ушёл с
            продуктовой поверхности вместе со всей методологией. Ряд вкладок остаётся —
            счётчик полей живёт на нём, а второй экран сюда ещё вернётся или нет, решает
            владелец, не разметка. */}
        <nav className="k-tabs" role="tablist" aria-label={T.app.tablistLabel}>
          <button type="button" role="tab" className="k-tab" aria-selected={true}>
            {T.app.tabMap}
            <span className="k-tab__count">{workspace.fields.length}</span>
          </button>
        </nav>

        <div className="k-head__right">
          {/* The operator has to be able to tell at a glance which surface he is looking at:
              in this mode a finished verdict is hidden until an observation is recorded, and
              a screen that does that without saying so reads as a broken screen. */}
          {workspace.verificationMode && <span className="k-badge">{T.app.verificationMode}</span>}
          {/* Whose fields these are. One instrument gets shared between people, and a list
              of somebody else's fields looks exactly like an empty list of your own. */}
          <span className="k-head__user" title={T.app.signedInAs(user.login)}>
            {user.login}
          </span>
          {/* «Числа» стоят РЯДОМ С ТЁМНОЙ ТЕМОЙ, а не внизу консоли (UX_SPEC §4.1): это
              предпочтение просмотра, такое же, как тема, и внизу прокручиваемого тела оно
              двигало на 84 px всё, что над ним. Положение несёт `aria-pressed`, поэтому
              подпись одна на оба положения; `title` называет содержимое режима. */}
          <button
            type="button"
            className="k-btn k-btn--ghost"
            aria-pressed={advanced}
            title={T.advanced.what}
            onClick={toggleAdvanced}
          >
            {T.advanced.chip}
          </button>
          <button type="button" className="k-btn k-btn--ghost" onClick={toggleTheme}>
            {theme === "dark" ? T.app.themeToLight : T.app.themeToDark}
          </button>
          <button type="button" className="k-btn k-btn--ghost" onClick={() => void api.logout()}>
            {T.app.logout}
          </button>
        </div>
      </header>

      <MapScreen workspace={workspace} theme={theme} animate={animate} />
    </div>
  );
}
